/**
 * Service-worker update lifecycle manager (R18 Wave 1.7-C1).
 *
 * Problem (original, Bug 1 — round-8-voice):
 *   VitePWA with `registerType: 'autoUpdate'` called skipWaiting() immediately when a new
 *   SW finished installing, causing a hard page reload mid voice-session.
 *   Fixed: switched to `registerType: 'prompt'` + this module; activation is idle-gated.
 *
 * Problem (1.7-C1 — SW staleness):
 *   There was NO periodic `registration.update()`, so a kiosk that never reloads (a kiosk
 *   screen stays on for the whole day/show) never discovers a new SW. The browser only calls
 *   `update()` automatically on navigation, which never happens on a fullscreen kiosk PWA.
 *   Additionally, the `controllerchange` listener was added directly to
 *   `navigator.serviceWorker` inside a Promise callback without ever being removed, causing a
 *   listener leak on StrictMode double-mount and on re-registration paths.
 *
 * Solution (1.7-C1):
 *   1. Periodic `registration.update()` every 60s — so the browser fetches the SW script
 *      byte-range and discovers a new version within one minute of it being deployed.
 *   2. Additional `update()` calls on `visibilitychange` (tab becomes visible) and `online`
 *      (network reconnect) — catches the case where a kiosk woke from sleep or recovered a
 *      network drop.
 *   3. Idle-gated activation — NEVER calls SKIP_WAITING during an active voice session
 *      (status !== 'idle' and status !== 'dropped'). SKIP_WAITING is sent only when the
 *      session is idle/disconnected so the reload never interrupts a visitor.
 *   4. `controllerchange` listener leak fix — the handler reference is stored; it is removed
 *      in the effect cleanup, and a `registeredRef` guard prevents the double-add that
 *      React's StrictMode double-invocation can cause.
 *   5. Single registration owner — `navigator.serviceWorker.ready` is resolved once and the
 *      cleanup reference (`cleanupFn`) captures only the listeners added in this effect
 *      invocation.
 *
 * Net result: after one manual reload to get THIS build, future deploys auto-apply on the
 * kiosk the next time the session is idle, with no human intervention.
 *
 * Returns `{ needRefresh, applyUpdate }` so callers can surface an "Update ready" banner:
 *  - `needRefresh`: true when a new SW is waiting to activate.
 *  - `applyUpdate`: manually trigger activation (only safe between sessions; the hook also
 *    does this automatically when the session next reaches 'idle').
 */

import { useEffect, useRef, useState } from 'react';
import { useDemoStore } from '../store';

/** How often we call registration.update() to check for a new SW. */
const POLL_INTERVAL_MS = 60_000; // 60 s

/**
 * Hook — call once at the app root (App.tsx).
 * Registers the SW and sets up session-aware deferred activation.
 */
export function useSessionSafeSwUpdate(): {
  needRefresh: boolean;
  applyUpdate: () => void;
} {
  const [needRefresh, setNeedRefresh] = useState(false);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  /**
   * Guard against the double-add that React StrictMode causes:
   * the effect runs twice in development (mount → unmount → mount). The `controllerchange`
   * listener is added inside a Promise callback, which fires asynchronously; without a guard
   * the second invocation can fire and add a second listener before the first cleanup runs.
   * Setting `registeredRef.current = true` after the first successful registration ensures
   * the second invocation exits early.
   *
   * Note: In production (no StrictMode double-mount) this guard is a no-op.
   */
  const registeredRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (registeredRef.current) return;
    registeredRef.current = true;

    const registration = navigator.serviceWorker.ready;

    /** Called when a new SW enters the 'installed' (waiting) state. */
    function onWaiting(sw: ServiceWorker) {
      waitingWorkerRef.current = sw;
      setNeedRefresh(true);

      // If the session is already idle or dropped, activate immediately.
      const status = useDemoStore.getState().status;
      if (status === 'idle' || status === 'dropped') {
        activateWaiting();
        return;
      }

      // Subscribe to the store (Zustand v5: listener receives (state, prevState)).
      // Activate the moment the session drops to idle (visitor hangs up / error / drop).
      // The unsub handle is captured so we clean up after the first idle transition.
      const unsub = useDemoStore.subscribe((state, prevState) => {
        const wasActive = prevState.status !== 'idle' && prevState.status !== 'dropped';
        const nowIdle = state.status === 'idle' || state.status === 'dropped';
        if (wasActive && nowIdle) {
          unsub();
          unsubRef.current = null;
          activateWaiting();
        }
      });
      unsubRef.current = unsub;
    }

    function activateWaiting() {
      const sw = waitingWorkerRef.current;
      if (!sw) return;
      // Tell the waiting SW to skip waiting — it will take control and trigger controllerchange.
      sw.postMessage({ type: 'SKIP_WAITING' });
      waitingWorkerRef.current = null;
      setNeedRefresh(false);
    }

    // ── Cleanup references for this effect invocation ──────────────────────────
    let cleanupFn: (() => void) | null = null;
    let updateInterval: ReturnType<typeof setInterval> | null = null;

    registration.then((reg) => {
      // ── Periodic + event-driven update() calls ─────────────────────────────
      //
      // `registration.update()` fetches the SW script byte-range; if the server returns a
      // new script the browser installs it. We trigger this:
      //  a) every 60s (kiosk never navigates, so the browser won't auto-check);
      //  b) when the page becomes visible (kiosk wake from sleep / screensaver);
      //  c) when the network comes back online (kiosk recovered a connection drop).
      //
      // Each call is fire-and-forget (the `updatefound` listener below picks up the result).

      function triggerUpdate() {
        reg.update().catch(() => {
          // Network offline or transient fetch error — safe to ignore; will retry next interval.
        });
      }

      updateInterval = setInterval(triggerUpdate, POLL_INTERVAL_MS);

      function onVisible() {
        if (document.visibilityState === 'visible') triggerUpdate();
      }
      function onOnline() {
        triggerUpdate();
      }

      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('online', onOnline);

      // ── New-SW detection ───────────────────────────────────────────────────
      //
      // A SW may already be waiting (e.g. the page loaded after a deploy but before closing).
      if (reg.waiting) {
        onWaiting(reg.waiting);
      }

      const onUpdateFound = () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', function onStateChange() {
          if (this.state === 'installed' && navigator.serviceWorker.controller) {
            // A new SW is now waiting to activate.
            onWaiting(this);
          }
        });
      };
      reg.addEventListener('updatefound', onUpdateFound);

      // ── controllerchange listener (leak-fixed) ─────────────────────────────
      //
      // When the SW takes control (after skipWaiting), reload to pick up the new assets.
      // The handler ref is stored so it can be removed in cleanup — the old code added the
      // listener inside a Promise callback with no way to remove it, leaking on re-mounts.
      function onControllerChange() {
        // A new SW just took control. The store should already be idle (we only call
        // skipWaiting when idle/dropped). Reload to activate the new version's assets.
        window.location.reload();
      }
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

      cleanupFn = () => {
        reg.removeEventListener('updatefound', onUpdateFound);
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('online', onOnline);
        if (updateInterval !== null) {
          clearInterval(updateInterval);
          updateInterval = null;
        }
      };
    });

    return () => {
      registeredRef.current = false;
      cleanupFn?.();
      unsubRef.current?.();
      unsubRef.current = null;
      if (updateInterval !== null) {
        clearInterval(updateInterval);
        updateInterval = null;
      }
    };
  }, []);

  const applyUpdate = () => {
    const status = useDemoStore.getState().status;
    if (status !== 'idle' && status !== 'dropped') {
      // Safety: refuse to apply mid-session. Callers should only surface this button
      // when status === 'idle'; this guard is a last-resort defensive check.
      console.warn('[sw-update] applyUpdate called during active session — deferred');
      return;
    }
    const sw = waitingWorkerRef.current;
    if (!sw) return;
    sw.postMessage({ type: 'SKIP_WAITING' });
    waitingWorkerRef.current = null;
    setNeedRefresh(false);
  };

  return { needRefresh, applyUpdate };
}
